import { Monitor, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";

const themeOrder = ["system", "light", "dark"] as const;
const themeIcons = { system: Monitor, light: Sun, dark: Moon } as const;
export const themeLabels = {
  system: "System",
  light: "Light",
  dark: "Dark",
} as const;

/** Current theme, the one after it in the cycle, and its icon. */
export function useThemeCycle() {
  const { theme, setTheme } = useTheme();
  const idx = themeOrder.indexOf(theme);
  const next = themeOrder[(idx + 1) % themeOrder.length];
  return { theme, setTheme, next, Icon: themeIcons[theme] };
}

/**
 * Icon button that cycles the theme. Page-surface control (workshop topbar):
 * it uses page tokens, so it does not belong inside the always-dark sidebar.
 */
export function ThemeToggle({ className }: { className?: string } = {}) {
  const { setTheme, next, Icon } = useThemeCycle();
  const label = `Switch to ${themeLabels[next]} theme`;
  return (
    <SimpleTooltip content={label}>
      <Button
        variant="ghost"
        size="icon"
        className={cn("h-8 w-8 text-muted-foreground", className)}
        onClick={() => setTheme(next)}
        aria-label={label}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </Button>
    </SimpleTooltip>
  );
}
